import { useState } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import MuiTab from '@mui/material/Tab';
import MuiTabs from '@mui/material/Tabs';
import Grid from '@mui/material/Grid';
import MGCViewOnly from '../shared/MGCViewOnly';
import ScopeViewOnly from '../shared/ScopeViewOnly';
import StrategyOverviewViewOnly from '../shared/StrategyOverviewViewOnly';
import CommentComp from './CommentComp';
import AttachmentComp from './AttachmentComp';
import SpendChartWrapper from './SpendChart/SpendChartWrapper';
import SupplierTabsCard from './Supplier/SupplierTabsCard';
import { SepPlansTab } from './SepPlans/SepPlansTab';
import {
  CHART_ALL_COLORS,
  CHART_All_DATASETS,
  CHART_COLORS,
  CHART_DATASETS,
  RENAME_MAP,
  RENAME_STRATEGY_MAP,
  SEP_PLANS
} from '../shared/AppConstants';
import { getSpendErrorMsg } from '../shared/UtilFunctions';

export const StrategyDetailsTabList = ({ value, onChange }) => (
  <MuiTabs
    selectionFollowsFocus={false}
    aria-label="view strategy tabs"
    value={value}
    onChange={(_, v) => onChange(v)}
  >
    <MuiTab label="Strategy Info" />
    <MuiTab label={SEP_PLANS} />
  </MuiTabs>
);

export const StrategyDetailsTabPanels = ({
  tabValue = 0,
  strategicSupplierDataGridApiRef,
  nonStrategicSupplierDataGridApiRef,
  strategyInformation,
  masterMGCJson,
  masterScopeJson,
  hasUSScope,
  userInfoMap,
  commonProps,
  spendGraphData,
  allMGCSpend,
  info
}) => {
  if (!strategyInformation) return null;

  const strategyInfoContent = (
    <>
      <Box mt={3}>
        <Grid container spacing={3} alignItems="flex-start">
          <Grid
            size={{ xs: 12, lg: 6 }}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Card>
              <CardContent>
                <Typography fontWeight="bold">
                  MGC (Material Group Code)
                </Typography>
                {masterMGCJson && (
                  <MGCViewOnly
                    strategyInformation={strategyInformation}
                    masterData={masterMGCJson}
                  />
                )}
              </CardContent>
            </Card>
            {masterScopeJson && (
              <ScopeViewOnly
                strategyInformation={strategyInformation}
                masterData={masterScopeJson}
                commonProps={commonProps}
                hasUSScope={hasUSScope}
              />
            )}
            <StrategyOverviewViewOnly
              strategyInformation={strategyInformation}
              userInfoMap={userInfoMap}
            />
            <CommentComp userInfoMap={userInfoMap} commonProps={commonProps} />
            <AttachmentComp
              key={strategyInformation?.attachments}
              commonProps={commonProps}
              attachments={strategyInformation?.attachments}
              userInfoMap={userInfoMap}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Card>
              <SpendChartWrapper
                data={spendGraphData}
                chart_colors={CHART_COLORS}
                chart_datasets={CHART_DATASETS}
                rename_map={RENAME_MAP}
                userId={info?.userId}
                title="Strategy Spend"
                subTitle="Invoiced & Forecasted spend for this strategy"
                noSpendErrMsg={getSpendErrorMsg(strategyInformation)}
                noSpendMsg="No spend for this strategy"
              />
            </Card>
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Card>
              <SpendChartWrapper
                data={allMGCSpend}
                chart_colors={CHART_ALL_COLORS}
                chart_datasets={CHART_All_DATASETS}
                rename_map={RENAME_STRATEGY_MAP}
                userId={info?.userId}
                title="All MGC Spend"
                subTitle="All John Deere spend in these MGC's"
                noSpendErrMsg={getSpendErrorMsg(strategyInformation, true)}
                noSpendMsg="No spend for MGC & Unit combination"
              />
            </Card>
          </Grid>
        </Grid>
      </Box>
      <Box mt={5}>
        <Card
          sx={(theme) => ({
            mt: theme.fuel6Tokens.JddcSpacingMedium,
            p: theme.fuel6Tokens.JddcSpacingMedium
          })}
        >
          <SupplierTabsCard
            strategicSupplierDataGridApiRef={strategicSupplierDataGridApiRef}
            nonStrategicSupplierDataGridApiRef={
              nonStrategicSupplierDataGridApiRef
            }
            readOnly={true}
            showSensitiveInfo={true}
            key={strategyInformation}
            strategyInformation={strategyInformation}
            supplierScopeOverview={strategyInformation?.scope}
            commonProps={commonProps}
            isEdit={false}
          />
        </Card>
      </Box>
    </>
  );

  return (
    <>
      <Box role="tabpanel" hidden={tabValue !== 0}>
        {strategyInfoContent}
      </Box>
      <Box role="tabpanel" hidden={tabValue !== 1}>
        <SepPlansTab
          commonProps={commonProps}
          masterScopeJson={masterScopeJson}
        />
      </Box>
    </>
  );
};

export const StrategyDetailsView = (props) => {
  const [tab, setTab] = useState(0);
  if (!props.strategyInformation) return null;

  return (
    <>
      <StrategyDetailsTabList value={tab} onChange={setTab} />
      <StrategyDetailsTabPanels tabValue={tab} {...props} />
    </>
  );
};

export default StrategyDetailsView;
